/**
 * The import's entry points (2.I-2 deliverables 1 and 2): the sidebar link and the Settings
 * section with the saved Foundry world, **Forget the saved world** and **Download the Codex macro**
 * (stage s2). Each draws nothing with
 * `flag:sheets` off. `foundry.world.clear` is this section's own control (DECISIONS 222 ruling 26).
 */
import { flagsFromMe, settingsGet, settingsUpdate } from '@codex/contracts';
import { Banner, Button } from '@codex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { api } from '../auth/client.js';
import { NavLink } from '../shell/nav-link.js';
import { useNotify } from '../shell/notices.js';
import { useGoTo } from '../shell/open-doc.js';
import { useShellSession } from '../shell/session.js';
import { foundryHref } from './href.js';
import { MacroLink } from './MacroLink.js';
import { SETTINGS_TEXT } from './model/copy.js';

/** The Settings page's own key, so a save there and Forget here share one answer. */
const settingsKey = ['settings', 'get'] as const;

/** True once the session is known and `flag:sheets` is on. */
export function useSheetsEnabled(): boolean {
  const me = useShellSession();
  return me !== null && flagsFromMe(me).sheets === true;
}

export function SidebarFoundryEntry() {
  return useSheetsEnabled() ? <SidebarLink /> : null;
}

function SidebarLink() {
  const pathname = useLocation({ select: (location) => location.pathname });
  return (
    <section className="cx-nav-section" aria-label="Foundry">
      <ul className="cx-nav-section__list">
        <li className="cx-nav-row-item">
          <NavLink
            href={foundryHref()}
            icon="upload"
            testId="shell.sidebar.foundry"
            active={pathname === '/foundry' || pathname.startsWith('/foundry/')}
          >
            Import from Foundry
          </NavLink>
        </li>
      </ul>
    </section>
  );
}

export function SettingsFoundrySection() {
  return useSheetsEnabled() ? <SettingsSection /> : null;
}

function SettingsSection() {
  const goTo = useGoTo();
  const notify = useNotify();
  const client = useQueryClient();
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: () => api(settingsGet),
    retry: false,
  });
  const forget = useMutation({
    mutationFn: () =>
      api(settingsUpdate, { params: { key: 'foundry_world_id' }, body: { value: null } }),
    onSuccess: (next) => {
      client.setQueryData(settingsKey, next);
      notify(SETTINGS_TEXT.forgotten);
    },
  });
  const world = settings.data?.foundry_world_id ?? null;
  return (
    <section aria-labelledby="settings-foundry" className="cx-settings__foundry">
      <h2 id="settings-foundry">{SETTINGS_TEXT.heading}</h2>
      <p>{SETTINGS_TEXT.intro}</p>
      <Button
        icon="upload"
        data-testid="settings.foundry.button"
        onClick={() => goTo(foundryHref())}
      >
        Import from Foundry
      </Button>
      <MacroLink />
      {settings.isSuccess && (
        <p className="cx-foundry-world">
          {world === null ? SETTINGS_TEXT.noWorld : SETTINGS_TEXT.world(world)}
          {world !== null && (
            <Button
              size="sm"
              variant="ghost"
              icon="x-circle"
              data-testid="foundry.world.clear"
              disabled={forget.isPending}
              onClick={() => forget.mutate()}
            >
              Forget the saved world
            </Button>
          )}
        </p>
      )}
      {forget.isError && (
        <Banner tone="danger" live="assertive">
          {SETTINGS_TEXT.forgetFailed}
        </Banner>
      )}
    </section>
  );
}
